import type { TableColumnTypeCustom } from "@/types/claims";
import { useUser } from "@/stores/user/user";
import { computed, type Ref, ref, watch, watchEffect } from "vue";

export type SavedColumn = {
  dataIndex: string;
  width: number;
  position: number;
  hide: boolean;
};

type NewColumn<T> = TableColumnTypeCustom<T> & {
  position: number;
};

export function useTableColumns<T>(
  columnsForInitRaw: Ref<TableColumnTypeCustom<T>[]>,
  columnsConfigName?: string,
  data?: Ref<T[]>,
) {
  const userStore = useUser();

  const dragList = ref<TableColumnTypeCustom[]>([]);

  const columnsForInit = computed(() =>
    columnsForInitRaw.value.filter(
      (c) => !c.claim || userStore.hasClaim(c.claim),
    ),
  );

  const initDragList = () => {
    const savedColumns = loadColumnsConfig();
    if (!savedColumns.length) {
      dragList.value = JSON.parse(JSON.stringify(columnsForInit.value));
      return;
    } else {
      dragList.value.length = 0;
    }

    // Массив для колонок, которых нет в сохраненных
    const newColumns: NewColumn<T>[] = [];

    // Подсчитываем количество колонок с 'left' и 'right'
    const fixedCounts = columnsForInit.value.reduce(
      (counts, column) => {
        if (column.fixed === "left") {
          counts.left += 1;
        } else if (column.fixed === "right") {
          counts.right += 1;
        }
        return counts;
      },
      { left: 0, right: 0 }, // Начальные значения для подсчета
    );

    // Добавляем колонки из columnsForInit на их позиции, а колонки, которых нет в savedColumns, сохраняем в новый массив
    columnsForInit.value.forEach((initCol, index) => {
      const savedCol = savedColumns.find(
        (el) => el?.dataIndex === initCol?.dataIndex,
      );

      if (savedCol) {
        const copyInitCol = JSON.parse(JSON.stringify(initCol));

        delete copyInitCol["fixed"];

        dragList.value[savedCol.position] = {
          ...copyInitCol,
          width: savedCol.width,
          hide: savedCol.hide,
          onFilter: initCol.onFilter,
        };
      } else {
        // Если колонка не найдена в сохраненных, добавляем её в newColumns
        newColumns.push({
          ...initCol,
          position: index,
        });
      }
    });

    // Вставляем новые колонки на нужные позиции, сдвигая другие
    newColumns.forEach((newCol) => {
      // Получаем позицию для вставки
      const position = newCol.position;

      // Сдвигаем элементы после нужной позиции на одну позицию вправо
      dragList.value.splice(position, 0, newCol);
    });

    // Присваиваем fixed: 'left' для первых fixedCounts.left колонок
    for (let i = 0; i < fixedCounts.left; i++) {
      if (dragList.value[i]) dragList.value[i].fixed = "left";
    }

    // Присваиваем fixed: 'right' для последних fixedCounts.right колонок
    for (
      let i = dragList.value.length - fixedCounts.right;
      i < dragList.value.length;
      i++
    ) {
      if (dragList.value[i]) dragList.value[i].fixed = "right";
    }

    // Удаляем пустые индексы из dragList
    dragList.value = dragList.value.filter((item) => item !== undefined);
  };

  const loadColumnsConfig = (): SavedColumn[] => {
    if (!columnsConfigName) return [];

    const userId = userStore.user?.id;
    if (!userId) return [];

    const storedData = localStorage.getItem(userId);
    const data = storedData ? JSON.parse(storedData) : {};

    return data[columnsConfigName] || [];
  };

  const saveColumnsConfig = () => {
    if (!columnsConfigName) return;

    const userId = userStore.user?.id;
    if (!userId) return;

    const storedData = localStorage.getItem(userId);

    const data = storedData ? JSON.parse(storedData) : {};

    data[columnsConfigName] = dragList.value.map((item, index) => ({
      dataIndex: item.dataIndex || "", // Гарантируем, что dataIndex всегда строка
      width: item.width || 0, // Если width не задан, то присваиваем 0
      position: index, // Индекс позиции
      hide: item.hide ?? false, // Если hide не задано, устанавливаем false по умолчанию
    }));

    localStorage.setItem(userId, JSON.stringify(data));
  };

  const columnsFiltered = computed<TableColumnTypeCustom[]>(() => {
    return dragList.value.filter((el) => {
      return !el.hide;
    });
  });

  const hiddenCols = computed(() => {
    return dragList.value.filter((el) => {
      return el.hide;
    });
  });

  const disabledCols = computed(() => {
    return dragList.value.filter((el) => {
      return el.disabled;
    });
  });

  const columnFilters = computed(() => {
    const filters: { [key: string]: Array<{ text: string; value: string }> } =
      {};

    dragList.value.forEach((el) => {
      // Проверяем, есть ли у элемента свойства onFilter и dataIndex
      if (!el.onFilter || !el.dataIndex || !data) {
        return; // Пропускаем, если хотя бы одно из свойств отсутствует
      }

      const uniqueValues = new Set(
        data.value.map(
          //@ts-ignore
          (product) => product[el.dataIndex as keyof typeof product],
        ),
      );

      filters[el.dataIndex] = Array.from(uniqueValues).map((value) => ({
        text: value as string,
        value: value as string,
      }));
    });

    return filters;
  });

  watch(
    () => dragList.value,
    () => {
      if (dragList.value.length - 1 === hiddenCols.value.length) {
        const lastNotHiddenItem = dragList.value.find((el) => !el.hide);
        if (lastNotHiddenItem) {
          lastNotHiddenItem.disabled = true;
        }
      } else if (disabledCols.value.length) {
        dragList.value.forEach((el) => (el.disabled = false));
      }
    },
    { deep: true },
  );

  watch(
    () => dragList.value,
    () => {
      if (dragList.value.length) {
        saveColumnsConfig();
      }
    },
    { deep: true },
  );

  watchEffect(() => {
    dragList.value.forEach((column) => {
      // Preserve explicitly configured filters (e.g. enum -> label mappings).
      if (Array.isArray(column.filters) && column.filters.length > 0) {
        return;
      }

      if (
        typeof column.dataIndex === "string" &&
        columnFilters.value[column.dataIndex]
      ) {
        column.filters = columnFilters.value[column.dataIndex];
      }
    });
  });

  return {
    columnsFiltered,
    dragList,
    hiddenCols,
    initDragList,
  };
}

const DEFAULT_TABLE_COLUMN_OPTIONS: Partial<TableColumnTypeCustom> = {
  minWidth: 100,
  maxWidth: 300,
  width: 150,
  resizable: true,
  align: "center",
};

export const initDefaultColumns = <T>(
  columns: TableColumnTypeCustom<T>[],
  defaultOptions: TableColumnTypeCustom = DEFAULT_TABLE_COLUMN_OPTIONS,
): TableColumnTypeCustom<T>[] =>
  columns.map(
    (column) => ({ ...defaultOptions, ...column }) as TableColumnTypeCustom<T>,
  );
